import React, { useCallback } from 'react'
import type { LoanContact } from '@matech/thebigpos-sdk'
import { useIntl } from 'react-intl'
import DataTable from '../DataTable'
import { getTheme } from '../../config'
import { formatDate } from '../../services/helper'
import ButtonGroup from '../Button/ButtonGroup'
import { useLoanContext } from '../../contexts/LoanContext'
import useLoanInvites from '../../hooks/useLoanInvites'
import { applyMask } from '../../services/utils'
import { addSpacesToPascalCase } from '../../services/helpers'
import Badge from '../Badge'
import useUser from '../../hooks/useUser'
import InviteModalButton from '../../pages/protected/people/components/InviteModalButton'
import InviteConfirmModal from '../../pages/protected/people/components/InviteConfirmModal'
import InviteIconButton from '../../pages/protected/people/components/InviteIconButton'
import MultiInviteButton from '../../pages/protected/people/components/MultiInviteButton'

const theme = getTheme()

const DEFAULT_SORT_COLUMN = 'createdAt'

const INVITE_WHITELIST_BY_ROLE: LoanContact['role'][] = [
	'Borrower',
	'CoBorrower',
	'NonBorrower',
	'LoanProcessor',
	'LoanOfficerAssistant',
	'SupportingLoanOfficer',
	'SettlementAgent',
	'TitleInsuranceAgent',
	'EscrowAgent',
]

const isInviteAllowed = (role: LoanContact['role']) => {
	return INVITE_WHITELIST_BY_ROLE.includes(role)
}

const fullName = (row: LoanContact) => {
	return row.firstName && row.lastName
		? `${row.firstName} ${row.lastName}`
		: row.fullName || 'Name Not Provided'
}

export type LoanContactsTableProps = {
	title?: string
}

const LoanContactsTable = ({ title }: LoanContactsTableProps) => {
	const { dataTable: breakpoint } = theme.breakpoints

	const [selectedRows, setSelectedRows] = React.useState<
		LoanContact[]
	>([])
	const [toggledClearRows, setToggleClearRows] = React.useState(false)
	const { formatMessage } = useIntl()
	const {
		loan,
		loanId,
		isLoading: isLoanLoading,
		isRefetching: isLoanRefetching,
		refetch,
	} = useLoanContext()
	const { inviteContacts, inviteContactsMutation, loanInvitesQuery } =
		useLoanInvites(loanId)
	const { isBorrower } = useUser()
	const [isInviteModalOpen, setIsInviteModalOpen] =
		React.useState(false)

	const {
		data: loanInvites,
		refetch: refetchLoanInvites,
		isRefetching: isLoanInvitesRefetching,
	} = loanInvitesQuery

	const refetchAllData = useCallback(() => {
		refetch?.()
		refetchLoanInvites()
	}, [refetch, refetchLoanInvites])

	const handleSelectedRowsChange = ({
		selectedRows,
	}: {
		selectedRows: LoanContact[]
	}) => {
		setSelectedRows(selectedRows)
	}

	const handleInviteContacts = React.useCallback(
		async (rows: LoanContact[]) => {
			if (!loan) {
				return
			}
			const { loanID } = loan
			const contactIds = rows.map((row) => row.id)

			await inviteContacts({
				loanId: loanID,
				contactIds,
			})

			refetchAllData()
		},
		[inviteContacts, loan, refetchAllData]
	)

	const hasExistingInvite = (row: LoanContact) => {
		return loanInvites?.data.some(
			(invite) => invite.emailAddress === row.email
		)
	}

	const rowDisabledCriteria = (row: LoanContact) =>
		hasExistingInvite(row) || !isInviteAllowed(row.role)

	const columns = [
		{
			name: formatMessage({ id: 'global.name' }),
			selector: (row: LoanContact) => fullName(row),
			cell: (row: LoanContact) => fullName(row),
			sortable: true,
		},
		{
			name: formatMessage({ id: 'global.email' }),
			selector: (row: LoanContact) => row.email || '',
			sortable: true,
		},
		{
			name: formatMessage({ id: 'global.phone_number' }),
			cell: (row: LoanContact) => applyMask(row.phone, 'phone'),
		},
		{
			name: formatMessage({ id: 'global.role' }),
			selector: (row: LoanContact) => addSpacesToPascalCase(row.role),
			sortable: true,
		},
		{
			name: formatMessage({ id: 'global.created' }),
			selector: (row: LoanContact) => row.createdAt || '',
			cell: (row: LoanContact) => formatDate(row.createdAt, false),
			sortable: true,
			sortField: 'createdAt',
			hide: breakpoint,
		},
		{
			name: '',
			omit: isBorrower,
			cell: (row: LoanContact) => {
				const handleInviteClick = () => {
					handleInviteContacts([row])
				}

				if (isLoanInvitesRefetching || !isInviteAllowed(row.role)) {
					return null
				}

				if (hasExistingInvite(row)) {
					return (
						<Badge
							label={formatMessage({ id: 'global.pending' })}
							color="info"
						/>
					)
				}

				const isRowBorrowerOrNonBorrower = [
					'Borrower',
					'CoBorrower',
					'NonBorrower',
				].includes(row.role)

				const Button = (() => {
					if (isRowBorrowerOrNonBorrower) {
						return (
							<InviteModalButton
								key={`${row.id}-invite`}
								onInviteClick={handleInviteClick}
							/>
						)
					}
					return <InviteIconButton onClick={handleInviteClick} />
				})()
				return <ButtonGroup overflowLimit={1} items={[Button]} />
			},
		},
	]

	const conditionalRowStyles = React.useMemo(() => {
		return [
			{
				when: (row: LoanContact) => selectedRows.includes(row),
				style: {
					backgroundColor: `${theme.siteConfig.color.primary}08 !important`,
				},
			},
		]
	}, [selectedRows])

	const contextActions = React.useMemo(() => {
		if (isBorrower) {
			return null
		}
		const handleClearRows = () => {
			setToggleClearRows(!toggledClearRows)
			setSelectedRows([])
		}

		const handleOpenInviteModal = () => {
			setIsInviteModalOpen(true)
		}

		const handleCloseInviteModal = () => {
			setIsInviteModalOpen(false)
		}

		const handleSendInvitesButtonClick = async () => {
			await handleInviteContacts(selectedRows)
			handleClearRows()
			setIsInviteModalOpen(false)
		}

		const isBorrowerOrNonBorrowerSelected = selectedRows.some((row) =>
			['Borrower', 'CoBorrower', 'NonBorrower'].includes(row.role)
		)

		return (
			<>
				<MultiInviteButton
					onClick={
						isBorrowerOrNonBorrowerSelected
							? handleOpenInviteModal
							: handleSendInvitesButtonClick
					}
					disabled={
						isLoanInvitesRefetching || selectedRows.length === 0
					}
					numberSelected={selectedRows.length}
				/>
				<InviteConfirmModal
					open={isInviteModalOpen}
					confirmButtonText={`${formatMessage({ id: 'invites.send_invite' })}${selectedRows.length > 0 ? ` (${selectedRows.length})` : ''}`}
					onConfirm={handleSendInvitesButtonClick}
					onClose={handleCloseInviteModal}
				/>
			</>
		)
	}, [
		formatMessage,
		handleInviteContacts,
		isBorrower,
		isInviteModalOpen,
		isLoanInvitesRefetching,
		selectedRows,
		toggledClearRows,
	])

	return (
		<div>
			<DataTable
				title={title}
				subHeader={
					<p className="mb-5">
						{formatMessage({ id: 'loans.loan_contacts_description' })}
					</p>
				}
				onRefreshClick={refetchAllData}
				data={loan?.contacts || []}
				columns={columns}
				defaultSortAsc={false}
				defaultSortFieldId={DEFAULT_SORT_COLUMN}
				progressPending={isLoanLoading && !isLoanRefetching}
				refreshing={inviteContactsMutation.isPending}
				keyField="id"
				titleActions={contextActions}
				selectableRows={!isBorrower}
				onSelectedRowsChange={handleSelectedRowsChange}
				clearSelectedRows={toggledClearRows}
				selectableRowDisabled={rowDisabledCriteria}
				conditionalRowStyles={conditionalRowStyles}
			/>
		</div>
	)
}

export default LoanContactsTable
